import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { t } from 'ttag';
import config from '../../../../config/channels';
import { operatorName } from '../../../../config/operator';
import useBasketContext from '../../../../contexts/basket/useBasketContext';
import useOperatorContext from '../../../../contexts/operator/useOperatorContext';
import { TSite } from '../../../../contexts/site/site-context';
import { APIErrorCode } from '../../../../services/api';
import { filterSites, getMenu } from '../../../../services/api/site';
import {
    trackSiteSelectChoose,
    trackSiteSelectComplete,
    trackSiteSelectSearch,
} from '../../../../services/siteSelectTracking';
import { CTA } from '../../../general/Button';
import LocationPicker, { LocationSummary, PickedLocation, PickerLocation } from './LocationPicker';
import ModalPanel from './ModalPanel';
import { ErrorMessage, Question } from './styles';

type SitesPanelProps = {
    channel: 'collection' | 'dine_in';
    // those that offer the channel, in the order to list them
    sites: TSite[];
    // called as customers choose, so the menu behind can follow
    onSelect?: (site: TSite) => void;
    focusRequest?: number;
};

// what the API says of a site that matches a search: how far it is, if that's what was searched for
type Match = { uid: string; distance?: number };

const SitesPanel = ({ channel, sites, onSelect, focusRequest }: SitesPanelProps) => {
    const navigate = useNavigate();
    const operatorContext = useOperatorContext();
    const basketContext = useBasketContext();
    const [searchParams] = useSearchParams();

    const isDineIn = channel === config.dineIn.name;
    const canSearch = operatorContext.operator?.theme.v4.location_search ?? false;

    const [query, setQuery] = useState('');
    // sites matching what was typed
    const [found, setFound] = useState<Match[] | null>(null);
    // sites by how far they are from the customer, or from the postcode in the link
    const [located, setLocated] = useState<Match[] | null>(null);
    const [locating, setLocating] = useState(false);
    const [locationError, setLocationError] = useState('');

    const [selected, setSelected] = useState<string | null>(null);
    const [step, setStep] = useState<'list' | 'table'>('list');
    const [table, setTable] = useState('');
    const [tableInvalid, setTableInvalid] = useState(false);
    const [loading, setLoading] = useState(false);

    const postcodeInLink = searchParams.get('postcode');

    useEffect(() => {
        if (!postcodeInLink) {
            return;
        }

        filterSites(operatorName, postcodeInLink)
            .then((data: Match[]) => setLocated(data))
            .catch(() => {});
    }, [postcodeInLink]);

    useEffect(() => {
        const search = query.trim();

        if (search.length < 3) {
            return;
        }

        // wait for typing to pause, and ignore an answer that comes after a newer question
        let current = true;

        const timer = setTimeout(() => {
            trackSiteSelectSearch('modal', channel, 'text');

            filterSites(operatorName, search)
                .then((data: Match[]) => current && setFound(data))
                .catch(() => current && setFound(null));
        }, 250);

        return () => {
            current = false;
            clearTimeout(timer);
        };
    }, [query]);

    const findNearest = () => {
        if (!navigator.geolocation) {
            setLocationError(t`Your browser can’t tell us where you are. Search by town or postcode instead.`);
            return;
        }

        trackSiteSelectSearch('modal', channel, 'location');

        setLocating(true);
        setLocationError('');

        navigator.geolocation.getCurrentPosition(
            ({ coords }) => {
                filterSites(operatorName, JSON.stringify({ lat: coords.latitude, lng: coords.longitude }))
                    .then((data: Match[]) => {
                        setQuery('');
                        setLocated(data);
                    })
                    .catch(() => setLocationError(t`Sorry, we couldn’t find the nearest locations. Please try again.`))
                    .finally(() => setLocating(false));
            },
            () => {
                setLocationError(t`We couldn’t find where you are. Search by town or postcode instead.`);
                setLocating(false);
            },
            { timeout: 10000 }
        );
    };

    const matches = query.trim().length > 2 ? found : located;

    let shown: { site: TSite; distance?: number }[];

    if (!matches) {
        shown = sites.map((site) => ({ site }));
    } else if (matches.some((match) => match.distance !== undefined)) {
        // searching for a place ranks every site by how far it is
        shown = sites
            .map((site) => ({ site, distance: matches.find((match) => match.uid === site.uid)?.distance }))
            .sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity));
    } else {
        shown = sites
            .filter((site) => matches.some((match) => match.uid === site.uid))
            .map((site) => ({ site }));
    }

    const describe = (site: TSite, distance?: number): PickerLocation => {
        const data = isDineIn ? site.channels.dine_in : site.channels.collection;
        const open = data?.is_open ?? false;

        return {
            id: site.handle,
            name: site.name,
            address: `${site.address_1}, ${site.address_city} ${site.address_postcode}`,
            open,
            status: open ? (data?.opening_times || t`Open now`) : t`Not currently accepting orders`,
            distance,
        };
    };

    const locations = shown.map(({ site, distance }) => describe(site, distance));

    const selectedSite = sites.find((site) => site.handle === selected);

    const select = (handle: string) => {
        const site = sites.find((site) => site.handle === handle);

        if (!site) {
            return;
        }

        setSelected(handle);
        onSelect?.(site);
        trackSiteSelectChoose('modal', channel, handle);

        if (isDineIn) {
            setTable('');
            setTableInvalid(false);
            setStep('table');
        }
    };

    const startDineIn = (site: TSite) => {
        const tableNumber = table.trim();

        if (!tableNumber) {
            return;
        }

        setLoading(true);
        setTableInvalid(false);

        basketContext.setTableNumber(tableNumber);

        getMenu(operatorName, site.handle, 'dine_in', operatorContext.operator?.flow ?? '', tableNumber)
            .then(() => {
                localStorage.setItem('table', tableNumber);
                trackSiteSelectComplete('modal', channel, site.handle);
                navigate(`/${config.dineIn.handle}/${site.handle}`);
            })
            .catch((error) => {
                if (error.code && [APIErrorCode.TableNotFound].includes(error.code)) {
                    setTableInvalid(true);
                    setLoading(false);
                } else {
                    navigate('/404');
                }
            });
    };

    const start = () => {
        if (!selectedSite) {
            return;
        }

        if (isDineIn) {
            startDineIn(selectedSite);
            return;
        }

        // a closed site can still be looked at
        const open = selectedSite.channels.collection?.is_open ?? false;

        setLoading(true);
        trackSiteSelectComplete('modal', channel, selectedSite.handle);
        navigate(`/${config.collection.handle}/${selectedSite.handle}${open ? '' : '?viewOnly=1'}`);
    };

    // at a table, it is the table that is needed, once the restaurant is chosen
    const ready = isDineIn ? step === 'table' && table.trim().length > 0 : !!selectedSite;

    const footer = (
        <CTA block disabled={!ready || loading} loading={loading} onClick={start}>
            <span>{t`View menu`}</span>
        </CTA>
    );

    if (isDineIn && step === 'table' && selectedSite) {
        return (
            <ModalPanel footer={footer} focusRequest={focusRequest}>
                <Back
                    type="button"
                    className="all-restaurants"
                    onClick={() => {
                        // no restaurant is chosen again, so any of them can be
                        setSelected(null);
                        setStep('list');
                        setTable('');
                        setTableInvalid(false);
                    }}
                >
                    <span className="material-symbols-outlined" aria-hidden="true">arrow_back</span>
                    <span className="label">{t`All restaurants`}</span>
                </Back>

                <PickedLocation>
                    <LocationSummary
                        location={locations.find((location) => location.id === selectedSite.handle) ?? describe(selectedSite)}
                    />
                </PickedLocation>

                <Question as="label" htmlFor="table-number">{t`What’s your table number?`}</Question>
                <Form.Control
                    id="table-number"
                    size="lg"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    autoFocus
                    placeholder={t`Enter table number`}
                    value={table}
                    onChange={(e) => {
                        setTable(e.target.value);
                        setTableInvalid(false);
                    }}
                    onKeyDown={(e: React.KeyboardEvent) => e.key === 'Enter' && ready && !loading && start()}
                />

                { tableInvalid && (
                    <ErrorMessage role="alert">
                        <span className="material-symbols-outlined" aria-hidden="true">info</span>
                        {t`Sorry, the table number is not valid.`}
                    </ErrorMessage>
                )}
            </ModalPanel>
        );
    }

    return (
        <ModalPanel footer={footer} focusRequest={focusRequest}>
            <LocationPicker
                question={isDineIn ? t`Which restaurant are you in?` : t`Where are you collecting from?`}
                label={t`Restaurants`}
                noun={{ one: t`restaurant`, many: t`restaurants` }}
                locations={locations}
                total={sites.length}
                selected={selected}
                onSelect={select}
                query={canSearch ? query : undefined}
                onQuery={canSearch ? setQuery : undefined}
                placeholder={t`Town or postcode`}
                onUseLocation={canSearch ? findNearest : undefined}
                locating={locating}
                error={locationError}
            />
        </ModalPanel>
    );
};

const Back = styled.button`
    align-self: flex-start;
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 0;
    border: 0;
    background: none;
    color: inherit;

    .label {
        text-decoration: underline;
        text-underline-offset: 4px;
    }

    .material-symbols-outlined {
        font-size: 18px;
        line-height: 18px;
    }
`;

export default SitesPanel;
